import React from "react";
import { connect } from "react-redux";
import _ from "lodash";
import "../../../css/sizeSelector.css";
import ProductSelector from "../services/ProductSelector";
import FeatureSwitches from "../../../config/FeatureSwitches";
import { updateDimensions } from "../redux/reducers/playerSlice";
import { resizeCanvas } from "../redux/creators/fabricCreators";
import { changeProduct } from "../redux/creators/threekitCreators";
import { setAddToCartDisabled } from "../redux/reducers/threekitSlice";
import {
  getDimensions,
  getHandle,
  titleForProduct,
  priceForProduct,
  hasRecommendedSize,
  isSingleCanvas,
} from "../helpers/productHelpers";
import { calculateRecommendedSize } from "../helpers/playerHelpers";
import { recommendedSizes } from "../../../config/recommendedSizes";
import styled from "styled-components";
import SizeSelectorDropdown from "./SizeSelectorDropdown";
import CustomSizeDropdown from "./CustomSizeDropdown";

class SizeSelector extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      products: [],
      features: {},
      recommendedProduct: null,
      selectedProduct: null,
      customProduct: null,
      selectedWidth: null,
      selectedHeight: null,
      showCustomSize: false
    };
  }

  componentDidMount() {
    /**
     * For single canvases, the products are based on set
     * recommended sizes. Otherwise products just need to
     * be fetched from the collection.
     */
    isSingleCanvas(this.props.collectionName)
      ? this.loadRecommendedCanvasProducts()
      : this.loadProducts();
    this.loadFeatures();

    if( window.innerWidth <= 800  ) {
      this.setState({ showCustomSize: false });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      imageWidth: prevImageWidth,
      imageHeight: prevImageHeight,
    } = prevProps.imageDimensions;
    const {
      imageWidth: imageWidth,
      imageHeight: imageHeight,
    } = this.props.imageDimensions;

    // After initial product loading, set initial product
    if (
      this.state.products.length > 0 &&
      this.state.products.length !== prevState.products.length
    ) {
      this.handleInitialProduct();
    }

    // If image is changed, restart process
    if (
      imageWidth !== prevImageWidth ||
      imageHeight !== prevImageHeight
    ) {
      this.setState({
        products : [],
        recommendedProduct : {},
      });
      isSingleCanvas(this.props.collectionName)
        ? this.loadRecommendedCanvasProducts()
        : this.loadProducts();
    }
  }

  newProductSelector() {
    return new ProductSelector(this.props.collectionName, this.config());
  }

  config() {
    if (isSingleCanvas(this.props.collectionName)) {
      return {
        enableCustomSizes: false,
        recommendedSizeEnabled: false,
        freeOption: window.location.search.includes("purl"),
      };
    } else if (hasRecommendedSize(this.props.collectionName)) {
      return {
        recommendedSizeEnabled: false,
      };
    } else {
      return {};
    }
  }

  loadFeatures() {
    const featureSwitch = new FeatureSwitches();
    this.state.features = featureSwitch.setFeatures();
  }

  loadProducts() {
    const productSelector = this.newProductSelector();
    productSelector.loadProducts((products) => {
      const handle = getHandle();
      if (handle) {
        products = products
          .filter((product) => product.handle.includes(handle))
          .sort((productA, productB) => {
            return getDimensions(productA)[0] - getDimensions(productB)[0];
          });
      }
      // If product needs a recommended size labeled
      if (this.config()?.recommendedSizeEnabled) {
        this.setRecommendedSize();
      }
      this.setState({ products });
    });
  }

  loadRecommendedCanvasProducts() {
    const productSelector = this.newProductSelector();

    // Calculate recommended size based on image size
    const {imageHeight, imageWidth} = this.props.imageDimensions;
    const [canvasHeight, canvasWidth] = calculateRecommendedSize(imageWidth, imageHeight);

    // Sort canvas recommended sizes
    const getRatio = ({ width, height }) => width / height;
    const imageRatio = canvasWidth / canvasHeight;
    const sortedSizes = recommendedSizes.sort((a, b) => {
      function getDifference(size) {
        return Math.abs(getRatio(size) - imageRatio);
      }
      return getDifference(a) - getDifference(b);
    });

    // Get the handles for the first 6 sorted products
    const sizeToHandle = ({ height, width }) => `${height}x${width}-canvas-photo-print`;
    let recommendedHandle = sizeToHandle(sortedSizes[0]);
    let handles = sortedSizes.slice(0, 6).map(sizeToHandle);
    const currentHandle = window.product?.handle || undefined;

    /**
     * Make sure if a specific size canvas is selected
     * that that size gets loaded in with the products
     * so that it is selected and listed in the dropdown
     */
    if (
      currentHandle !== undefined &&
      currentHandle !== "customize-your-canvas" &&
      !handles.includes(currentHandle)
    ) {
      handles.push(currentHandle);
    }

    // If PURL page, set free 8x8 as recommended
    if (this.config().freeOption) {
      const index = handles.indexOf("8x8-canvas-photo-print");
      const freeOption = `free-8-x-8-square-canvas-print`;
      if (index) {
        handles[index] = freeOption;
      } else {
        handles.push(freeOption);
      }
      recommendedHandle = freeOption;
    }

    // Load and set recommended product
    productSelector.loadProduct(
      recommendedHandle,
      (recommendedProduct) => {
        this.setState({ recommendedProduct });
      },
      false
    );

    // Set products from sorted handles
    productSelector.loadProductsByHandles(
      handles,
      (products) => {
        this.setState({ products });
      }
    );
  }

  handleInitialProduct() {
    /**
     * The generic canvas page gets a recommended size
     * based on the uploaded image set by default, otherwise
     * load the default product
     */
    if (
      window.product?.handle === "customize-your-canvas" ||
      this.props.selectedProduct.handle === "customize-your-canvas"
    ) {
      return this.loadRecommendedProduct();
    } else {
      return this.getDefaultProduct();
    }
  }

  getDefaultProduct() {
    const defaultProduct = this.state.products.find(
      (product) => product.id === window.product?.id
    );
    const initialProduct = defaultProduct || this.state.products[0];
    this.handleProductChange(initialProduct);
  }

  loadRecommendedProduct() {
    const productSelector = this.newProductSelector();
    const recommendedProduct = this.state.recommendedProduct;
    productSelector.loadProduct(
      recommendedProduct.handle,
      (product) => {
        this.handleProductChange(product);
      },
      false
    );
  }

  findProduct = (productToFind) => {
    // Find the selected product from dropdown selection
    const foundProduct =
      this.state.products.find((product) => product.id === productToFind.id) ||
      null;

    // If a product is found, change to that product
    if (foundProduct !== null) {
      this.handleProductChange(foundProduct);
    }
  };

  async handleProductChange(product) {
    // Disable cart
    this.props.setAddToCartDisabled(true);

    // Process change
    const dimensions = getDimensions(product);
    this.props.changeProduct(product);
    this.props.updateDimensions({
      canHeight: dimensions[0],
      canWidth: dimensions[1],
    });
    await this.props.resizeCanvas(dimensions[0], dimensions[1]);
    this.setState({
      selectedProduct: product,
      customProduct: null,
    });

    // Enable cart
    this.props.setAddToCartDisabled(false);
  };

  setRecommendedSize() {
    const productSelector = this.newProductSelector();
    productSelector.loadProducts((products) => {
      // Get image ratio and orientation
      const { imageHeight: height, imageWidth: width } = this.props.imageDimensions;
      const ratio = height / width;
      const orientation = height > width;

      // Filter out sizes that are too big or the wrong orientation
      const productsArray = productSelector.productDimensionsArray(products);
      const filteredArray = productsArray.filter((product) => {
        return (
          (product.width <= width || product.height <= height) &&
          orientation === product.orientation
        );
      });

      // Set the recommended product
      let recommendedProduct = {};
      if (filteredArray.length > 0) {
        const closest = filteredArray.reduce((a, b) => {
          return Math.abs(b.ratio - ratio) < Math.abs(a.ratio - ratio) ? b : a;
        });
        recommendedProduct = products.find(
          (product) => product.handle == closest.handle
        );
      } else {
        recommendedProduct = orientation ? products[2] : products[0];
      }
      this.setState({ recommendedProduct });
    });
  }

  loadCustomProduct(height, width) {
    let handle = `${height}x${width}-canvas-photo-print`;
    if (this.config().freeOption && height === 8 && width === 8) {
      handle = `free-8-x-8-square-canvas-print`;
    }
    $.getJSON(`/products/${handle}.json`, (response) => {
      this.handleProductChange(response.product);
      this.setState({
        customProduct: response.product,
      });
      this.props.changeProduct(response.product);
    });
  }

  updateHeight = (event) => {
    const height = event.value;
    const maxWidth = 61;
    this.setState({
      selectedHeight: height,
      features: {
        ...this.state.features,
        maxSingleCanvasWidth: maxWidth,
      },
    });
    if (this.state.selectedWidth) {
      this.loadCustomProduct(height, this.state.selectedWidth);
    }
  };

  updateWidth = (event) => {
    const width = event.value;
    const maxHeight = 61;
    this.setState({
      selectedWidth: width,
      features: {
        ...this.state.features,
        maxSingleCanvasHeight: maxHeight,
      },
    });
    if (this.state.selectedHeight) {
      this.loadCustomProduct(this.state.selectedHeight, width);
    }
  };

  get sortedProducts() {
    return this.state.products.sort((productA, productB) => {
      function getPrice(product) {
        return product?.variants?.[0]?.price || 0;
      }
      return getPrice(productA) - getPrice(productB);
    });
  }

  get selectOptions() {
    // Checks if a product is the recommended product
    const isRecommended = (product) => {
      return product.id === this.state.recommendedProduct?.id;
    };

    // Creates products object to use for React select
    return this.sortedProducts.map((product) => {
      return {
        id: product.id,
        label: titleForProduct(product),
        price: priceForProduct(product),
        recommended: isRecommended(product),
        value: product.id,
      };
    });
  }

  render() {

    return (
      <Container>
        {this.sortedProducts.length > 0 && (
          <SizeSelectorDropdown
            options={this.selectOptions}
            selectedProduct={this.state.selectedProduct}
            findProduct={this.findProduct}
            showCustomSize={this.state.showCustomSize}
          />
        )}
        {window.is_ab_custom_pdp && this.config()?.enableCustomSizes && (
          <div>
            <label className="custom-sizelabel">
              <input
                type="checkbox"
                onChange={({ target: { checked } }) =>
                  this.setState({ showCustomSize: checked })
                }
                checked={this.state.showCustomSize}
              />
              Custom size?
            </label>
          </div>
        )}
        {this.config()?.enableCustomSizes && !window.is_ab_custom_pdp && (
          <CustomSizeDropdown
            state={this.state}
            updateHeight={this.updateHeight}
            updateWidth={this.updateWidth}
          />
        )}
        {/** SHOW CUSTOM SIZE SELECTOR FOR AB TEST     **/}
        {this.config()?.enableCustomSizes &&
          window.is_ab_custom_pdp &&
          this.state.showCustomSize && (
            <CustomSizeDropdown
              state={this.state}
              updateHeight={this.updateHeight}
              updateWidth={this.updateWidth}
            />
          )}
      </Container>
    );
  }
}

const mapStateToProps = (state) => {
  const { imageHeightOriginal, imageWidthOriginal } =
    state.playerState.finalObj;
  return {
    imageDimensions: {
      imageHeight: imageHeightOriginal,
      imageWidth: imageWidthOriginal
    }
  };
};

export default connect(mapStateToProps, {
  updateDimensions,
  changeProduct,
  resizeCanvas,
  setAddToCartDisabled,
})(SizeSelector);

const Container = styled.div`
  width: 100%;

  .custom-sizelabel {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    input {
      accent-color: #EB0067;
      width: 18px;
      height: 18px;
    }
  }
`;
